<template>
  <div class="flex-1 overflow-y-auto p-8 space-y-6">

    <!-- ================= TOP STATS ================= -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">

      <!-- Reputation Score -->
      <div class="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <div class="flex justify-between items-start mb-4">
          <h3 class="text-sm font-bold text-slate-500 uppercase tracking-tight">
            Reputation Score
          </h3>

          <!-- <span class="text-green-600 bg-green-50 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
            <span class="material-symbols-outlined text-sm">trending_up</span>
            +12%
          </span> -->
        </div>

        <div class="flex items-baseline gap-2">
          <span class="text-4xl font-black text-slate-900">{{ reputationScore }}</span>

          <div class="flex text-yellow-400">
            <span v-for="i in Math.floor(reputationScore)" :key="'full-' + i" class="material-symbols-outlined fill-1">
              star
            </span>

            <span v-if="reputationScore % 1 >= 0.5" class="material-symbols-outlined fill-1">
              star_half
            </span>
          </div>
        </div>

        <p class="mt-2 text-sm text-slate-500 font-medium">
          Based on {{ totalReviews }} verified reviews
        </p>
      </div>

      <!-- GMB Health -->
      <div class="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-center gap-6">
        <!-- <div class="w-24 h-24 shrink-0 rounded-full radial-progress flex items-center justify-center">
          <span class="text-xl font-black text-slate-800">{{ gmbHealth }}%</span>
        </div> -->

        <div class="flex-1">
          <h3 class="text-sm font-bold text-slate-500 uppercase tracking-tight mb-1">
            Review Health
          </h3>

          <div class="flex items-center gap-2 mb-2">
            <div class="relative w-16 h-16">
                <svg class="w-16 h-16 transform -rotate-90">
                  <!-- background -->
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="#e2e8f0"
                    stroke-width="6"
                    fill="none"
                  />

                  <!-- progress -->
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="#22c55e"
                    stroke-width="6"
                    fill="none"
                    stroke-linecap="round"
                    :stroke-dasharray="175.9"
                    :stroke-dashoffset="175.9 - (175.9 * gmbHealth) / 100"
                  />
                </svg>

                <div class="absolute inset-0 flex items-center justify-center text-sm font-bold text-slate-700">
                  {{ gmbHealth }}%
                </div>
              </div>
            <span class="text-lg font-bold text-slate-800">{{gmbHealthTyp}}</span>
          </div>

          <p class="text-xs text-slate-500 leading-relaxed">
            Keep your information updated to maintain strong visibility.
          </p>
        </div>
      </div>

      <!-- Customer Sentiment -->
      <div class="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <h3 class="text-sm font-bold text-slate-500 uppercase tracking-tight mb-4">
          Customer Sentiment
        </h3>

        <div class="space-y-4">

          <div>
            <div class="flex justify-between text-xs font-bold mb-1">
              <span class="text-slate-700">Happy</span>
              <span class="text-slate-700">{{ sentiment.happy }}%</span>
            </div>

            <div class="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
              <div class="bg-green-500 h-full rounded-full" :style="{ width: sentiment.happy + '%' }"></div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">

            <div>
              <div class="flex justify-between text-xs font-bold mb-1">
                <span class="text-slate-500">Neutral</span>
                <span>{{ sentiment.neutral }}%</span>
              </div>

              <div class="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                <div class="bg-slate-400 h-full rounded-full" :style="{ width: sentiment.neutral + '%' }"></div>
              </div>
            </div>

            <div>
              <div class="flex justify-between text-xs font-bold mb-1">
                <span class="text-slate-500">Unhappy</span>
                <span>{{ sentiment.unhappy }}%</span>
              </div>

              <div class="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                <div class="bg-red-400 h-full rounded-full" :style="{ width: sentiment.unhappy + '%' }"></div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- ================= MIDDLE SECTION ================= -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

      <!-- Review Growth -->
      <div class="lg:col-span-2 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">

        <div class="flex justify-between items-center mb-6">
          <div>
            <h3 class="text-base font-bold text-slate-800">Review Growth</h3>
            <p class="text-xs text-slate-500">Last 30 days performance</p>
          </div>

          <!-- <div class="flex gap-2"> -->
          <!-- <button class="px-3 py-1 text-xs font-bold bg-slate-50 border border-slate-200 rounded-lg">
              Export
            </button> -->

          <!-- <button class="px-3 py-1 text-xs font-bold bg-white border border-slate-200 rounded-lg">
              Month
            </button> -->
          <!-- </div> -->
        </div>

        <!-- ✅ ApexChart Container -->
        <div class="chart-wrapper">
          <div id="reviewGrowthChart" class="chart-container"></div>
        </div>

      </div>

      <!-- SMS Campaign -->
      <div class="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col">

        <div class="flex items-center gap-2 mb-6">
          <span class="material-symbols-outlined text-primary">email</span>
          <h3 class="text-base font-bold text-slate-800">Highlevel Overview</h3>
        </div>

        <div class="space-y-6 flex-1">

          <MetricTmp title="Total Review campaigns" :value="campaign.delivery" icon="done_all" color="text-green-500" />

          <MetricTmp title="Total Referral Campaigns" :value="campaign.openRate" icon="drafts" color="text-primary" />

          <MetricTmp title="Total QR Created" :value="campaign.conversion" icon="ads_click" color="text-blue-400" />

        </div>
        <button class="mt-6 w-full py-2 bg-slate-800 text-white text-sm font-bold rounded-lg hover:bg-slate-700">
          View Reviews Report
        </button>
      </div>
    </div>

    <!-- ================= NEW ANALYTICS STRIP ================= -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

      <!-- Rating Distribution -->
      <div class="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <h3 class="text-base font-bold text-slate-800 mb-4">
          Rating Distribution
        </h3>

        <!-- ✅ ApexChart Container -->
        <!-- <div id="ratingDistributionChart"></div> -->
        <div class="chart-wrapper">
          <div id="ratingDistributionChart" class="chart-container"></div>
        </div>

      </div>

      <!-- Platform Breakdown (Future Ready Placeholder) -->
      <div class="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <h3 class="text-base font-bold text-slate-800 mb-4">
          Platform Performance
        </h3>

        <div class="text-xs text-slate-400">
          Google vs Facebook vs Others
        </div>
        <div class="chart-wrapper">
          <div id="platformChart" class="chart-container"></div>
        </div>
      </div>

      <!-- Sentiment Trend (Future Ready Placeholder) -->
      <div class="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <h3 class="text-base font-bold text-slate-800 mb-4">
          Sentiment Trend
        </h3>

        <div class="mt-4 space-y-3">

          <!-- AI Insight Badge -->
         
           <div>
            <div class="flex justify-between text-xs font-bold mb-1">
              <span class="text-slate-700">Sentimate</span>
              <span class="text-slate-700">{{ sentimentMeta.change }}%</span>
            </div>

            <div class="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
              <div class="bg-green-500 h-full rounded-full" :style="{ width: sentimentMeta.change + '%' }"></div>
            </div>
          </div>


          <p class="text-xs text-slate-500 leading-relaxed mb-4" style="margin-top: 2rem;">
            {{ sentimentMeta.improvementDetails }}
          </p>
        </div>
      </div>

    </div>

    <!-- ================= BOTTOM SECTION ================= -->
    <div class="">

      <!-- Recent Reviews -->
      <div class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">

        <div class="p-6 border-b border-slate-100 flex justify-between items-center">
          <h3 class="text-base font-bold text-slate-800">Recent Reviews</h3>
          <a href="/review-list" class="text-xs font-bold text-primary hover:underline">View All</a>
        </div>

        <div class="divide-y divide-slate-50">

          <!-- <ReviewTmp v-for="r in recent_reviews" :key="r.review_id"
              :name="r.review_name" 
              :text="r.review_feedback" 
              :platform="r.review_platform" 
              :time="beautifyDate(r.review_datetime)"
              :rating="r.review_rating"
              :status="r.review_status" /> -->

              <ReviewTmp
                v-for="r in recent_reviews"
                :key="r.review_id"
                :name="r.review_name"
                :text="r.review_feedback"
                :platform="r.review_platform"
                :time="beautifyDate(r.review_datetime)"
                :rating="r.review_rating"
                :status="r.review_status"
                @ai-reply="openAiReply(r)"
              />

        </div>

      </div>


    </div>
<!-- AI Reply Modal -->
<div v-if="aiModal" class="fixed inset-0 bg-black/40 flex items-center justify-center z-50">

  <div class="bg-white w-full max-w-lg rounded-xl shadow-xl p-6">

    <div class="flex justify-between items-center mb-4">
      <h3 class="text-lg font-bold text-slate-800">AI Reply</h3>

      <button
        class="text-slate-500 hover:text-slate-700"
        @click="aiModal=false"
      >
        ✕
      </button>
    </div>

    <textarea
      v-model="aiReplyText"
      rows="6"
      class="w-full border border-slate-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
      placeholder="Write or generate AI reply..."
    ></textarea>

    <div class="flex justify-end gap-2 mt-4">

      <button
        class="px-4 py-2 text-sm font-bold bg-slate-100 rounded-lg"
        @click="aiModal=false"
      >
        Cancel
      </button>

      <button
        class="px-4 py-2 text-sm font-bold bg-primary text-white rounded-lg"
        @click="sendReply"
      >
        Send Reply
      </button>

    </div>

  </div>
</div>
  </div>
</template>
<script>
  import ApexCharts from "apexcharts";
  import MetricTmp from "@/comps/MetricTmp.vue";
  // import ActionTmp from "@/comps/ActionTmp.vue";
  import ReviewTmp from "@/comps/ReviewTmp.vue";
  import {useToast} from 'vue-toastification';

  import {
    dashboardStats,
    reviewGrowth,
    ratingDistribution,
    platformPerformance,
    sentimentTrend,
    recentReviews,
    getSentimentTrd,
    sendAiReplay
  } from "../apis/dashapis";

  export default {
    components: { MetricTmp, ReviewTmp },

    data() {
      return {
        aiModal: false,
        aiReplyText: "",
        selectedReview: null,
        sentimentMeta: {
          trend: "",        // up | down | stable
          change: 0,          // % change vs last period
          riskLevel: "",   // Low | Medium | High
          improvementTip: "",
          improvementDetails: ""
        },
        gmbHealth: 0,
        gmbHealthTyp: "Worst",
        review_user: "",

        reputationScore: 0,
        totalReviews: 0,

        sentiment: {
          happy: 0,
          neutral: 0,
          unhappy: 0
        },

        campaign: {
          delivery: 0,
          openRate: 0,
          conversion: 0
        },

        growthChart: null,
        ratingChart: null,
        platformChart: null,
        sentimentChart: null,

        recent_reviews: []
      };
    },
    created(){
        const tmp = sessionStorage.getItem("ldata") || "";

      if (tmp && tmp.length > 0) {
        const ldata = JSON.parse(tmp);

        if (ldata) {
          this.review_user = ldata.user_id;
          
        }
      }
    },
    mounted() {
      this.toast = useToast();
      const vm = this;

      setTimeout(function() {
        vm.loadDashboard();
      }, 3000);
    },

    methods: {
      async sendReply() {
          if (!this.aiReplyText) return;

          console.log("Replying to:", this.selectedReview.review_id);
          console.log("Reply:", this.aiReplyText);

           const res1 = await sendAiReplay(this.aiReplyText,this.selectedReview.review_id);
            if (res1.status) {
              this.aiModal = false;
              this.toast.success("Replay Sent!!");
              this.loadRecentReviews();
            }
            else{
              this.toast.error("Failed");
            }

          
        },
      openAiReply(review) {
        this.selectedReview = review;
        this.aiReplyText = "";
        this.aiModal = true;
      },
      beautifyDate(datetime) {
        if (!datetime) return "";

        const date = new Date(datetime.replace(" ", "T"));

        return date.toLocaleString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit"
        });
      },
      /* ================= HELPERS ================= */

      getInitials(name) {
        if (!name) return "NA";

        return name
          .split(" ")
          .map(n => n[0])
          .join("")
          .substring(0, 2)
          .toUpperCase();
      },

      formatTime(datetime) {
        if (!datetime) return "";
        return datetime;
      },

      getReviewColor(rating) {
        rating = parseInt(rating);

        if (rating >= 4) return "blue";
        if (rating === 3) return "purple";
        return "red";
      },

      /* ================= MASTER LOADER ================= */

      async loadDashboard() {
        this.loadStats(),
          this.loadGrowth(),
          this.loadRatings(),
          this.loadPlatforms(),
          this.loadSentimentTrend(),
          this.loadRecentReviews()
      },

      /* ================= STATS ================= */

      async loadStats() {
        const res = await dashboardStats(this.review_user);
        if (!res.status) return;

        const data = res.data;

        this.reputationScore = parseFloat(data.avg_rating || 0);
        this.totalReviews = parseInt(data.total_reviews || 0);

        const happy = parseInt(data.sentiment?.happy || 0);
        const neutral = parseInt(data.sentiment?.neutral || 0);
        const unhappy = parseInt(data.sentiment?.unhappy || 0);

        const total = happy + neutral + unhappy || 1;

        this.sentiment = {
          happy: Math.round((happy / total) * 100),
          neutral: Math.round((neutral / total) * 100),
          unhappy: Math.round((unhappy / total) * 100)
        };

        this.campaign = {
          delivery: parseInt(data.total_reviews || 0),
          openRate: parseInt(data.total_referral || 0),
          conversion: parseInt(data.total_qr || 0)
        };
      },

      /* ================= REVIEW GROWTH ================= */

      async loadGrowth() {
        const res1 = await getSentimentTrd(this.review_user);
        if (res1.status) {
          const sntDt = res1.data;
          if(sntDt!=null && sntDt!=undefined){
            this.sentiment.happy = sntDt.happy;
            this.sentiment.neutral = sntDt.neutral;
            this.sentiment.unhappy = sntDt.unhappy;
            this.sentimentMeta.change = sntDt.sentiment_percentage;
            this.sentimentMeta.improvementDetails = sntDt.detailed_explanation+"";
            this.gmbHealth = sntDt.review_health_percentage;
            this.gmbHealthTyp = sntDt.review_health==='none'?"Worst":sntDt.review_health || "Worst";
            await this.$nextTick();
          }
        }

       
        const res = await reviewGrowth(this.review_user);
         if (res.status) {
          const rows = res.data;

          const categories = rows.map(r => r.dt);
          const seriesData = rows.map(r => parseInt(r.total));
          await this.$nextTick();

          if (this.growthChart) this.growthChart.destroy();

          this.growthChart = new ApexCharts(
            document.querySelector("#reviewGrowthChart"),
            {
              chart: { type: "area", height: 260, toolbar: { show: false } },
              stroke: { curve: "smooth", width: 3 },
              dataLabels: { enabled: false },
              colors: ["#197fe6"],
              series: [{ name: "Reviews", data: seriesData }],
              xaxis: { categories }
            }
          );

          this.growthChart.render();
         }

        
      },

      /* ================= RATING DISTRIBUTION ================= */

      async loadRatings() {
        const res = await ratingDistribution(this.review_user);
        if (!res.status) return;

        const rows = res.data;

        const labels = rows.map(r => `${r.rating} Stars`);
        const series = rows.map(r => parseInt(r.total));

        await this.$nextTick();

        if (this.ratingChart) this.ratingChart.destroy();

        this.ratingChart = new ApexCharts(
          document.querySelector("#ratingDistributionChart"),
          {
            chart: {
              type: "donut",
              height: 260,
              animations: {
                enabled: true,
                easing: "easeinout",
                speed: 800
              }
            },

            series,

            labels,

            colors: [
              "#16a34a",  // 5 star - green
              "#22c55e",  // 4 star
              "#facc15",  // 3 star
              "#fb923c",  // 2 star
              "#ef4444"   // 1 star - red
            ],

            stroke: {
              width: 0
            },

            legend: {
              position: "bottom",
              fontSize: "12px",
              fontWeight: 600,
              markers: {
                radius: 12
              }
            },

            dataLabels: {
              enabled: false
            },

            plotOptions: {
              pie: {
                donut: {
                  size: "65%",
                  labels: {
                    show: true,
                    name: {
                      show: true,
                      fontSize: "13px",
                      color: "#64748b"
                    },
                    value: {
                      show: true,
                      fontSize: "18px",
                      fontWeight: 700,
                      color: "#0f172a"
                    },
                    total: {
                      show: true,
                      label: "Total",
                      fontSize: "13px",
                      color: "#64748b",
                      formatter: () => {
                        return series.reduce((a, b) => a + b, 0);
                      }
                    }
                  }
                }
              }
            },

            tooltip: {
              y: {
                formatter: val => `${val} reviews`
              }
            }
          }
        );

        this.ratingChart.render();
      },

      /* ================= PLATFORM PERFORMANCE ================= */

      async loadPlatforms() {
        const res = await platformPerformance(this.review_user);
        if (!res.status) return;

        const rows = res.data;

        const categories = rows.map(r => r.platform);
        const seriesData = rows.map(r => parseInt(r.total));

        await this.$nextTick();

        if (this.platformChart) this.platformChart.destroy();

        this.platformChart = new ApexCharts(
          document.querySelector("#platformChart"),
          {
            chart: { type: "bar", height: 260, toolbar: { show: false } },
            series: [{ name: "Reviews", data: seriesData }],
            xaxis: { categories },
            dataLabels: { enabled: false }
          }
        );

        this.platformChart.render();
      },

      /* ================= SENTIMENT TREND ================= */

      async loadSentimentTrend() {
        const res = await sentimentTrend(this.review_user);
        if (!res.status) return;

        const rows = res.data;


        if (this.sentimentChart) this.sentimentChart.destroy();

        this.sentimentChart = new ApexCharts(
          document.querySelector("#sentimentChart"),
          {
            chart: { type: "line", height: 260, toolbar: { show: false } },
            series: [{
              name: "Happy",
              data: rows.map(r => r.happy)
            }],
            xaxis: {
              categories: rows.map(r => r.label)
            }
          }
        );

        this.sentimentChart.render();
      },

      /* ================= RECENT REVIEWS ================= */

      async loadRecentReviews() {
        const res = await recentReviews(this.review_user);
        if (!res.status) return;

        this.recent_reviews = res.data;
      }

    }
  };
</script>

<style scoped>
  /* ================= MOBILE OPTIMIZATION ================= */

  @media (max-width: 1024px) {

    /* Global padding reduction */
    .dashboard-padding {
      padding: 1rem !important;
    }

    /* Card spacing */
    .dashboard-grid {
      gap: 0.75rem !important;
    }

    /* Chart height fix */
    .chart-container {
      height: 13rem !important;
    }

    /* Buttons scale down */
    button {
      padding: 0.6rem 0.8rem !important;
    }

    /* Text scaling */
    h3 {
      font-size: 0.85rem !important;
    }

    /* Large numbers (stats) */
    .stat-number {
      font-size: 2rem !important;
    }

    /* Review items stack */
    .review-item {
      flex-direction: column !important;
    }

    /* Avatar spacing */
    .review-avatar {
      margin-bottom: 0.5rem;
    }
  }


  /* ================= SMALL MOBILE ================= */

  @media (max-width: 640px) {

    /* Extra compact spacing */
    .dashboard-grid {
      gap: 0.5rem !important;
    }

    /* Even smaller chart */
    .chart-container {
      height: 11rem !important;
    }

    /* Buttons tighter */
    button {
      font-size: 0.75rem !important;
    }

    /* Prevent oversized cards */
    .dashboard-card {
      padding: 0.9rem !important;
    }
  }

  .chart-wrapper {
    width: 100%;
    height: 260px;
    overflow: hidden;
    /* ✅ prevents bleed */
  }

  .chart-wrapper .apexcharts-canvas {
    max-width: 100% !important;
  }
</style>